import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ArchivedJobYear,
  Job,
  JobBoard,
  JobCadence,
  StudentJobHistory,
} from '@td/shared';
import { del, get, patch, post } from '../lib/api';

/**
 * The job board.
 *
 * Two queries rather than one: the jobs themselves barely ever change and are
 * wanted on the dashboard as well, while a board is per cadence and per
 * period and changes every time a name is dragged. Keeping them apart means
 * dropping a child onto Line Leader refetches one turn's board, not the whole
 * year.
 */
const JOBS = ['jobs'];
const BOARD = ['job-board'];

export function useJobs() {
  return useQuery({
    queryKey: JOBS,
    queryFn: () => get<{ jobs: Job[] }>('/jobs').then((r) => r.jobs),
  });
}

export function useJobBoard(cadence: JobCadence, period: string) {
  return useQuery({
    queryKey: [...BOARD, cadence, period],
    queryFn: () => get<JobBoard>(`/jobs/board?cadence=${cadence}&period=${period}`),
  });
}

/**
 * A school year that has been put away.
 *
 * Its own query key rather than the board's: an archived year cannot change,
 * so nothing that happens on this year's board should ever refetch it.
 */
export function useArchivedJobYear(year: string | null) {
  return useQuery({
    queryKey: ['job-year', year],
    queryFn: () => get<ArchivedJobYear>(`/jobs/year/${encodeURIComponent(year ?? '')}`),
    enabled: !!year,
  });
}

export function useStudentJobHistory(studentId: string | null) {
  return useQuery({
    queryKey: ['job-history', studentId],
    queryFn: () => get<StudentJobHistory>(`/jobs/history/${studentId}`),
    enabled: !!studentId,
  });
}

/*
 * Every change refetches the board.
 *
 * Reconciling by hand would mean recomputing the flags and the counts in the
 * client — the same arithmetic in a second place, which is how two answers to
 * "how many times has she been line leader" come to exist.
 */
function useRefresh() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: BOARD });
    void qc.invalidateQueries({ queryKey: ['job-history'] });
  };
}

export interface AssignInput {
  jobId: string;
  studentId: string;
  period: string;
}

export function useAssignJob() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (input: AssignInput) => post('/jobs/assign', input),
    onSuccess: refresh,
  });
}

export function useUnassignJob() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (input: AssignInput) => post('/jobs/unassign', input),
    onSuccess: refresh,
  });
}

export function useFillJobs() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (input: { cadence: JobCadence; period: string }) =>
      post<{ filled: number }>('/jobs/fill', input),
    onSuccess: refresh,
  });
}

export function useClearJobs() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (input: { cadence: JobCadence; period: string }) =>
      post<{ cleared: number }>('/jobs/clear', input),
    onSuccess: refresh,
  });
}

export interface JobInput {
  name: string;
  cadence: JobCadence;
  description: string;
  wants: number;
}

/** Adding, renaming or deleting a job changes the board as well as the list. */
function useRefreshAll() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: JOBS });
    void qc.invalidateQueries({ queryKey: BOARD });
  };
}

export function useCreateJob() {
  const refresh = useRefreshAll();
  return useMutation({
    mutationFn: (input: JobInput) => post<Job>('/jobs', input),
    onSuccess: refresh,
  });
}

export function useUpdateJob() {
  const refresh = useRefreshAll();
  return useMutation({
    mutationFn: ({ id, ...input }: Partial<JobInput> & { id: string }) =>
      patch<Job>(`/jobs/job/${id}`, input),
    onSuccess: refresh,
  });
}

export function useDeleteJob() {
  const refresh = useRefreshAll();
  return useMutation({
    mutationFn: (id: string) => del(`/jobs/job/${id}`),
    onSuccess: refresh,
  });
}
